import { enumToOptions } from "../../utils/enumToOptions";
import {
  DealType,
  PaymentType,
  EquityType,
  ClauseType,
} from "@quantizr-engineering/common/types/brand-deal";
import { DocMap, FieldsMap } from "./";
import hourglass from "../../assets/hourglass-01.svg";

const summaryFieldsMap: FieldsMap = {
  partiesAndTerms: {
    name: "partiesAndTerms",
    label: "Parties and Terms",
    type: "text",
    subfields: [
      {
        name: "talent",
        label: "Talent",
        type: "text",
        required: true,
      },
      {
        name: "brand",
        label: "Brand",
        type: "text",
        required: true,
      },
      {
        name: "brandAgency",
        label: "Brand Agency",
        type: "text",
        required: false,
      },
      {
        name: "dealType",
        label: "Deal Type",
        type: "select",
        options: enumToOptions(DealType),
        required: true,
      },
      {
        name: "effectiveDate",
        label: "Effective Date",
        type: "date",
        required: false,
      },
      {
        name: "term",
        label: "Term",
        type: "text",
        required: true,
      },
    ],
  },

  keyPointSummaries: {
    name: "keyPointSummaries",
    label: "Key Point Summaries",
    type: "text",
    subfields: [
      {
        name: "paymentSummary",
        label: "Payment",
        type: "text",
        required: false,
      },
      {
        name: "deliverablesSummary",
        label: "Deliverables",
        type: "text",
        required: false,
      },
      {
        name: "exclusivitySummary",
        label: "Exclusivity",
        type: "text",
        required: false,
      },
    ],
  },
  // TODO: Implement clauses (needs a custom filter function)
  //   notableClauses: {
  //     name: "notableClauses",
  //     label: "Notable Clauses",
  //     type: "text",
  //     subfields: [],
  //   },
  additionalNotes: {
    name: "additionalNotes",
    label: "Additional Notes",
    type: "text",
    subfields: [
      {
        name: "additional_points",
        label: "Additional Notes",
        type: "textArray",
        required: false,
      },
    ],
  },
};
const dataFieldsMap: FieldsMap = {
  talent: {
    name: "talent",
    label: "Talent",
    type: "text",
    required: true,
  },
  talentAgency: {
    name: "talentAgency",
    label: "Talent Agency",
    type: "text",
    required: true,
  },
  brand: {
    name: "brand",
    label: "Brand",
    type: "text",
    required: true,
  },
  brandAgency: {
    name: "brandAgency",
    label: "Brand Agency",
    type: "text",
    required: false,
  },
  effectiveDate: {
    name: "effectiveDate",
    label: "Effective Date",
    type: "date",
    required: false,
  },
  term: {
    name: "term",
    label: "Term",
    type: "text",
    required: true,
  },
  terminationDate: {
    name: "terminationDate",
    label: "Termination Date",
    type: "date",
    required: false,
  },
  exclusivitySummary: {
    name: "exclusivitySummary",
    label: "Exclusivity Summary",
    type: "text",
    required: false,
  },
  dealType: {
    name: "dealType",
    label: "Deal Type",
    type: "select",
    options: enumToOptions(DealType),
    required: true,
  },
  paymentSummary: {
    name: "paymentSummary",
    label: "Payment Summary",
    type: "text",
    required: true,
  },
  renewalAutomatic: {
    name: "renewalAutomatic",
    label: "Automatic Renewal",
    type: "boolean",
    required: true,
  },
  renewalCompensation: {
    name: "renewalCompensation",
    label: "Renewal Compensation",
    type: "text",
    required: false,
  },
  renewalTerm: {
    name: "renewalTerm",
    label: "Renewal Term",
    type: "text",
    required: false,
  },
  deliverablesSummary: {
    name: "deliverablesSummary",
    label: "Deliverables Summary",
    type: "text",
    required: true,
  },
  payments: {
    name: "payments",
    label: "Payments",
    type: "array",
    required: true,
    subfields: [
      {
        name: "paymentType",
        label: "Payment Type",
        type: "select",
        options: enumToOptions(PaymentType),
        required: true,
      },
      {
        name: "cashPayment",
        label: "Cash Payment",
        type: "currency",
        required: false,
        showSum: true,
      },
      {
        name: "cashPaymentDue",
        label: "Cash Payment Due",
        type: "date",
        required: false,
      },
      {
        name: "cashDeposit",
        label: "Cash Deposit",
        type: "currency",
        required: false,
        showSum: true,
      },
      {
        name: "cashDepositDate",
        label: "Cash Deposit Date",
        type: "date",
        required: false,
      },
      {
        name: "equityComp",
        label: "Equity Compensation",
        type: "text",
        required: false,
      },
      {
        name: "equityType",
        label: "Equity Type",
        type: "select",
        options: enumToOptions(EquityType),
        required: false,
      },
      {
        name: "equityQuantity",
        label: "Equity Quantity",
        type: "number",
        required: false,
      },
      {
        name: "vestingSummary",
        label: "Vesting Summary",
        type: "text",
        required: false,
      },
      {
        name: "vestedDate",
        label: "Vested Date",
        type: "date",
        required: false,
      },
      {
        name: "performanceThreshhold",
        label: "Performance Threshold",
        type: "text",
        required: false,
      },
      {
        name: "performanceCompensation",
        label: "Performance Compensation",
        type: "text",
        required: false,
      },
    ],
  },
  deliverables: {
    name: "deliverables",
    label: "Deliverables",
    type: "array",
    required: true,
    subfields: [
      {
        name: "item",
        label: "Item",
        type: "text",
        required: true,
      },
      {
        name: "details",
        label: "Details",
        type: "text",
        required: true,
      },
      {
        name: "dueDate",
        label: "Due Date",
        type: "date",
        required: false,
      },
      {
        name: "cadence",
        label: "Cadence",
        type: "text",
        required: false,
      },
      {
        name: "quantity",
        label: "Quantity",
        type: "number",
        required: false,
      },
    ],
  },
  exclusivities: {
    name: "exclusivities",
    label: "Exclusivities",
    type: "array",
    required: false,
    subfields: [
      {
        name: "term",
        label: "Term",
        type: "text",
        required: true,
      },
      {
        name: "terminationDate",
        label: "Termination Date",
        type: "date",
        required: true,
      },
      {
        name: "description",
        label: "Description",
        type: "text",
        required: true,
      },
    ],
  },
  clauses: {
    name: "clauses",
    label: "Clauses",
    type: "array",
    required: true,
    subfields: [
      {
        name: "type",
        label: "Type",
        type: "select",
        options: enumToOptions(ClauseType),
        required: true,
      },
      {
        name: "isPresent",
        label: "Is Present",
        type: "boolean",
        required: true,
      },
      {
        name: "notes",
        label: "Notes",
        type: "textArray",
        required: true,
        subfields: [
          {
            name: "note",
            label: "Note",
            type: "text",
            required: true,
          },
        ],
      },
    ],
  },
  additional_points: {
    name: "additional_points",
    label: "Additional Points",
    type: "array",
    required: true,
    subfields: [
      {
        name: "point",
        label: "Point",
        type: "text",
        required: true,
      },
    ],
  },
};

export const brandDealsDocMap: DocMap = {
  summaryFields: summaryFieldsMap,
  dataFields: dataFieldsMap,
  entityHrefTitle: () => "Coming soon...",
  entityHrefIcon: () => hourglass,
  entityHrefSubtitle: () => "",
  entityHref: null,
};
